import { useEffect, useMemo, useState, type ReactNode } from 'react';

import { useGetBoardForms } from 'api/forms';
import { useSearchParams } from 'hooks';
import type { Form } from 'types';

import { FormsListContext } from './FormsListContext';

type Props = {
  children: ReactNode;
};

export const FormsListContextProvider = ({ children }: Props) => {
  const [massActionsSelectedForms, setMassActionsSelectedForms] = useState<
    Form[]
  >([]);
  const { getSearchParam } = useSearchParams([
    'page',
    'id',
    'search',
    'from',
    'to',
  ]);
  const { data, isError, isLoading } = useGetBoardForms({
    page: getSearchParam('page'),
    id: getSearchParam('id'),
    name: getSearchParam('search'),
    updatedFrom: getSearchParam('updatedFrom'),
    updatedTo: getSearchParam('updatedTo'),
  });
  const [newFormWizardOpen, setNewFormWizardOpen] = useState(false);

  const massActionsSelectedFormsIds = massActionsSelectedForms.map(
    form => form.id,
  );
  const allFormsMassActionsSelected =
    data !== undefined
      ? data.data.length > 0 &&
        data.data.every(form => massActionsSelectedFormsIds.includes(form.id))
      : false;
  const isFormMassActionsSelected = (id: number) => {
    return massActionsSelectedFormsIds.includes(id);
  };

  const toggleFormMassActionsSelected = (form: Form) => {
    setMassActionsSelectedForms(prev =>
      prev.some(({ id: formId }: Form) => formId === form.id)
        ? prev.filter(({ id: formId }: Form) => formId !== form.id)
        : [...prev, form],
    );
  };

  const toggleAllMassActionsSelected = () => {
    if (data !== undefined) {
      setMassActionsSelectedForms(prev => (prev.length > 0 ? [] : data.data));
    }
  };

  useEffect(() => {
    setMassActionsSelectedForms([]);
  }, [data]);

  const value = useMemo(
    () => ({
      data,
      isError,
      isLoading,
      massActionsSelectedForms,
      isFormMassActionsSelected,
      toggleFormMassActionsSelected,
      allFormsMassActionsSelected,
      toggleAllMassActionsSelected,
      newFormWizardOpen,
      setNewFormWizardOpen,
    }),
    [data, isError, isLoading, massActionsSelectedForms, newFormWizardOpen],
  );

  return (
    <FormsListContext.Provider value={value}>
      {children}
    </FormsListContext.Provider>
  );
};
